import { t, te } from "@/language/translation";
import { isLISA3 } from "@/composables/useLISA3";
import {
  createRouter,
  createWebHistory,
  type RouteLocationRaw,
  type RouteRecordRaw,
} from "vue-router";
import { isModuleActive } from "./functions/moduleConfig";
import store from "./store";
import { captureMessageInSentry } from "./sentry";
import { ENV } from "./ENV";
import { installUnsavedChangesNavigationGuard } from "./functions/useUnsavedChanges";
import {
  GetSiteIdForBoardDocument,
  ResolveBDEProductionPlanTemplateAuthorizationSiteDocument,
  ResolveBDEDataSourceAuthorizationSiteDocument,
  ResolveBDEChecklistConnectionAuthorizationSiteDocument,
  ResolveBDELabelAuthorizationSiteDocument,
  ResolveBDEProcessAuthorizationSiteDocument,
  ResolveBDEViewAuthorizationSiteDocument,
  ResolveBDEViewAuthorizationSitesDocument,
  ResolveCostCenterAuthorizationSiteDocument,
  ResolveIHEquipmentAuthorizationSiteDocument,
  ResolveIHConfigAuthorizationSiteDocument,
  ResolveNewsAuthorizationSitesDocument,
  ResolveQAErrorCatalogueAuthorizationSiteDocument,
  ResolveQAChecklistAuthorizationSiteDocument,
  ResolveQAProductFamilyAuthorizationSiteDocument,
  ResolveTCSiteConfigAuthorizationSiteDocument,
  ResolveTechnicalPlaceAuthorizationSiteDocument,
  ResolveUserAuthorizationSiteDocument,
  ResolveUserShiftScheduleAuthorizationSiteDocument,
  type GetSiteIdForBoardQuery,
  type ResolveBDEProductionPlanTemplateAuthorizationSiteQuery,
  type ResolveBDEDataSourceAuthorizationSiteQuery,
  type ResolveBDEChecklistConnectionAuthorizationSiteQuery,
  type ResolveBDELabelAuthorizationSiteQuery,
  type ResolveBDEProcessAuthorizationSiteQuery,
  type ResolveBDEViewAuthorizationSiteQuery,
  type ResolveBDEViewAuthorizationSitesQuery,
  type ResolveCostCenterAuthorizationSiteQuery,
  type ResolveIHEquipmentAuthorizationSiteQuery,
  type ResolveIHConfigAuthorizationSiteQuery,
  type ResolveNewsAuthorizationSitesQuery,
  type ResolveQAErrorCatalogueAuthorizationSiteQuery,
  type ResolveQAChecklistAuthorizationSiteQuery,
  type ResolveQAProductFamilyAuthorizationSiteQuery,
  type ResolveTCSiteConfigAuthorizationSiteQuery,
  type ResolveTechnicalPlaceAuthorizationSiteQuery,
  type ResolveUserAuthorizationSiteQuery,
  type ResolveUserShiftScheduleAuthorizationSiteQuery,
} from "./graphql";
import { authorizationReady, hasRight, hasRightOnAnySite } from "./functions/authorizationState";
import {
  authorizationResourceId,
  checkRouteRights,
  isSiteInAuthorizationScope,
  matchesAuthorizationParentId,
  nonEmptyRouteParam,
  type RouterAccessRightMetaType,
} from "./functions/routeAuthorization";

const PageNotFound = () => import("./views/PageNotFound.vue");

const Roles = () => import("./views/RoleList.vue");
const RoleAssignment = () => import("./modules/organization/views/OGRoleAssignment.vue");

const DatapointTypes = () => import("./views/DatapointTypes.vue");
const DatapointType = () => import("./views/DatapointType.vue");
const BoardTemplates = () => import("./views/BoardTemplates.vue");
const BoardTemplate = () => import("./views/BoardTemplate.vue");

const SettingsColor = () => import("./views/systemSettings/SettingsColor.vue");
const GeneralSystemSettings = () => import("./views/systemSettings/GeneralSystemSettings.vue");
const DashLists = () => import("./views/Dashlist/Dashlists.vue");
const DashlistEditor = () => import("./views/Dashlist/DashlistEditor.vue");

const Changelog = () => import("./views/ChangelogView.vue");

const Redirect = () => import("./views/RedirectPage.vue");
// import routes from license
const licenseRouterAdditions: RouteRecordRaw[] = [];
const licenseRouters: Record<string, { default: RouteRecordRaw[] }> = import.meta.glob(
  "./license/**/router.ts",
  {
    eager: true,
  },
);

Object.entries(licenseRouters).forEach(([path, module]) => {
  if (isModuleActive(path.slice(2).split("/")[1])) {
    licenseRouterAdditions.push(...module.default);
  }
});

// import routes from modules
const modulesRouterAdditions: RouteRecordRaw[] = [];
const modulesRouters: Record<string, { default: RouteRecordRaw[] }> = import.meta.glob(
  "./modules/**/router.ts",
  {
    eager: true,
  },
);

Object.entries(modulesRouters).forEach(([_path, module]) => {
  modulesRouterAdditions.push(...module.default);
});

const routes: RouteRecordRaw[] = [
  {
    path: "/datapointTypes",
    name: "datapointTypes",
    component: DatapointTypes,
    meta: {
      rights: ["sqdc.admin"],
    },
  },
  {
    path: "/datapointType/:id",
    name: "datapointType",
    component: DatapointType,
    meta: {
      rights: ["sqdc.admin"],
    },
    props: (route) => ({ datapointTypeId: route.params.id }),
  },
  {
    path: "/boardTemplates",
    name: "boardTemplates",
    component: BoardTemplates,
    meta: {
      rights: ["sqdc.admin"],
    },
  },
  {
    path: "/boardTemplate/:id",
    name: "boardTemplate",
    component: BoardTemplate,
    meta: {
      rights: ["sqdc.admin"],
    },
    props: (route) => ({
      create: route.params.id === "new",
      boardTemplateId: route.params.id === "new" ? undefined : Number(route.params.id),
    }),
  },
  {
    path: "/roles",
    name: "roles",
    component: Roles,
    meta: {
      rights: ["systemSettings.admin"],
    },
  },
  {
    path: "/roleAssignment",
    name: "roleAssignment",
    component: RoleAssignment,
    meta: {
      rights: ["user.admin"],
    },
    props: true,
  },
  {
    path: "/createRole",
    name: "RolesCreate",
    component: () => import("./views/RolesCreate.vue"),
    meta: {
      rights: ["systemSettings.admin"],
    },
  },
  {
    path: "/updateRole/:id(\\d+)",
    name: "RolesUpdate",
    component: () => import("./views/RolesUpdate.vue"),
    meta: {
      rights: ["systemSettings.admin"],
    },
    props: (route) => ({
      id: Number(route.params.id),
    }),
  },
  {
    path: "/dashlists",
    name: "dashlists",
    component: DashLists,
    meta: {
      rights: ["sqdc.admin"],
    },
  },
  {
    path: "/dashlists/create",
    name: "CreateDashlist",
    component: DashlistEditor,
    meta: {
      rights: ["sqdc.admin"],
    },
  },
  {
    path: "/dashlists/:id(\\d+)",
    name: "UpdateDashlist",
    component: DashlistEditor,
    meta: {
      rights: ["sqdc.admin"],
    },
    props: (route) => ({
      id: Number(route.params.id),
    }),
  },
  {
    path: "/settings",
    name: "settings",
    meta: {
      rights: ["systemSettings.admin"],
    },
    children: [
      {
        path: "color",
        name: "settingsColor",
        meta: {
          rights: ["systemSettings.admin"],
        },
        component: SettingsColor,
      },
      {
        path: "general",
        name: "generalSettings",
        meta: {
          rights: ["systemSettings.admin"],
        },
        component: GeneralSystemSettings,
      },
    ],
  },
  {
    path: "/dashboard/edit",
    name: "dashboardEdit",
    component: () => import("./modules/dashboard/views/DashboardRouteView.vue"),
    beforeEnter: () =>
      store.getters["login/isAnonym"] || !isLISA3.value ? { name: "dashboard" } : true,
  },
  {
    // Filler site for menu entries without a link
    path: "/dashboard",
    name: "dashboard",
    component: () => import("./modules/dashboard/views/DashboardRouteView.vue"),
  },
  {
    path: "/dashboard/achievements",
    name: "achievements",
    component: () => import("./modules/dashboard/views/AchievementsView.vue"),
    beforeEnter: () =>
      store.getters["login/isAnonym"] || !isLISA3.value ? { name: "dashboard" } : true,
  },
  {
    path: "/changelog/:new",
    name: "ChangelogNew",
    component: Changelog,
  },
  {
    path: "/changelog",
    name: "Changelog",
    component: Changelog,
  },
  ...licenseRouterAdditions,
  ...modulesRouterAdditions,
  {
    // Filler site for menu entries without a link
    path: "/redirect/:moduleName",
    name: "redirect",
    component: Redirect,
    props: (route) => ({ moduleName: route.params.moduleName }),
  },
  {
    path: "/:pathMatch(.*)*",
    name: "pageNotFound",
    component: PageNotFound,
    beforeEnter(to) {
      if (to.path === "/404") {
        return;
      }

      captureMessageInSentry(`routeNotFound - ${to.path}`, {
        level: "warning",
        tags: {
          route: to.path,
        },
        extra: {
          path: to.path,
          queryKeys: Object.keys(to.query),
        },
      });
    },
  },
];

const router = createRouter({
  history: createWebHistory("/"),
  routes,
});

installUnsavedChangesNavigationGuard(router);

const routerPush = router.push;
router.push = function push(to: RouteLocationRaw) {
  return routerPush(to).catch((err) => {
    throw err;
  });
};

const routerReplace = router.replace;
router.replace = function replace(to: RouteLocationRaw) {
  return routerReplace(to).catch((err) => {
    throw err;
  });
};

export default router;

router.afterEach((to, _from) => {
  const softwareName = [import.meta.env.DEV ? "DEV" : "LISA"];
  if (ENV.VITE_APP_BRAND_NAME === "EMMA") {
    softwareName[0] = "EMMA";
  }
  const routeName = to.name?.toString();
  const routeKey = `router.${routeName}`;
  if (routeName === "achievements") {
    softwareName.push(` | ${t("modules.dashboard.achievements.title")}`);
  } else if (te(routeKey) || import.meta.env.DEV) {
    softwareName.push(` | ${t(routeKey)}`);
  }
  document.title = softwareName.join("");
});

export type { RouterAccessRightMetaType } from "./functions/routeAuthorization";

export function routerRightCheck(
  meta?: RouterAccessRightMetaType,
  siteIdParam?: string | string[] | number,
) {
  return checkRouteRights(meta, siteIdParam, hasRight, hasRightOnAnySite);
}

router.beforeEach(async (to, from) => {
  if (to.meta.rights && !authorizationReady.value && store.getters["login/isLogin"]) {
    const { refreshAuthorization } = await import("./functions/useAuthorization");
    await refreshAuthorization();
  }
  if (to.meta.rights && !authorizationReady.value) return "/404";
  if (to.path === "/") {
    if (store.getters["login/initialPage"] != null && store.getters["login/initialPage"] !== "/") {
      return store.getters["login/initialPage"];
    }
    return true;
  }
  const boardIdParam = nonEmptyRouteParam(to.params.boardId);
  const tpIdParam = to.params.tpId ?? to.query.tpId;
  const costCenterIdParam = to.params.costCenterId ?? to.query.costCenterId;
  const authorizationSiteResource = (to.meta as RouterAccessRightMetaType)
    .authorizationSiteResource;
  const resourceId =
    boardIdParam ??
    authorizationResourceId(to.meta as RouterAccessRightMetaType, to.params) ??
    tpIdParam ??
    costCenterIdParam;
  if (to.meta.rights && authorizationSiteResource && resourceId === "-1") {
    return "/404";
  }
  if (
    to.meta.rights &&
    authorizationSiteResource &&
    resourceId == null &&
    !(to.meta as RouterAccessRightMetaType).authorizationSiteResourceOptional
  ) {
    return "/404";
  }
  if (to.meta.rights && resourceId !== undefined && resourceId !== "-1") {
    const id = Number(resourceId);
    if (!Number.isInteger(id) || id <= 0) return "/404";
    try {
      const { apolloClient } = await import("./graphql/apollo");
      let siteId: number | undefined;
      let siteIds: number[] | undefined;
      if (boardIdParam !== undefined) {
        const { data } = await apolloClient.query<GetSiteIdForBoardQuery>({
          query: GetSiteIdForBoardDocument,
          variables: { boardId: id },
          fetchPolicy: "network-only",
        });
        siteId = data.GetSQDCBoard.siteId;
      } else if (authorizationSiteResource === "bdeViewRead") {
        const { data } = await apolloClient.query<ResolveBDEViewAuthorizationSitesQuery>({
          query: ResolveBDEViewAuthorizationSitesDocument,
          variables: { id },
          fetchPolicy: "network-only",
        });
        siteIds = data.BDEViewAuthorizationSites;
        const selectedSiteParam = to.params.siteId;
        if (selectedSiteParam !== undefined) {
          if (!isSiteInAuthorizationScope(siteIds, selectedSiteParam)) {
            return "/404";
          }
        }
        if (tpIdParam !== undefined) {
          const technicalPlaceId = Number(tpIdParam);
          if (!Number.isInteger(technicalPlaceId) || technicalPlaceId <= 0) return "/404";
          const technicalPlace =
            await apolloClient.query<ResolveTechnicalPlaceAuthorizationSiteQuery>({
              query: ResolveTechnicalPlaceAuthorizationSiteDocument,
              variables: { id: technicalPlaceId },
              fetchPolicy: "network-only",
            });
          if (!isSiteInAuthorizationScope(siteIds, technicalPlace.data.GetTechnicalPlace.siteId)) {
            return "/404";
          }
        }
      } else if (tpIdParam !== undefined) {
        const { data } = await apolloClient.query<ResolveTechnicalPlaceAuthorizationSiteQuery>({
          query: ResolveTechnicalPlaceAuthorizationSiteDocument,
          variables: { id },
          fetchPolicy: "network-only",
        });
        siteId = data.GetTechnicalPlace.siteId;
      } else if (authorizationSiteResource === "ihEquipment") {
        const { data } = await apolloClient.query<ResolveIHEquipmentAuthorizationSiteQuery>({
          query: ResolveIHEquipmentAuthorizationSiteDocument,
          variables: { id },
          fetchPolicy: "network-only",
        });
        siteId = data.GetIHEquipment.siteId;
      } else if (authorizationSiteResource === "ihConfig") {
        const { data } = await apolloClient.query<ResolveIHConfigAuthorizationSiteQuery>({
          query: ResolveIHConfigAuthorizationSiteDocument,
          variables: { id },
          fetchPolicy: "network-only",
        });
        siteId = data.IHConfigs.find((config) => config.id === id)?.siteId;
      } else if (authorizationSiteResource === "qaChecklist") {
        const { data } = await apolloClient.query<ResolveQAChecklistAuthorizationSiteQuery>({
          query: ResolveQAChecklistAuthorizationSiteDocument,
          variables: { id },
          fetchPolicy: "network-only",
        });
        siteId = data.QAChecklists.find((checklist) => checklist.id === id)?.siteId;
      } else if (authorizationSiteResource === "qaProductFamily") {
        const { data } = await apolloClient.query<ResolveQAProductFamilyAuthorizationSiteQuery>({
          query: ResolveQAProductFamilyAuthorizationSiteDocument,
          variables: { id },
          fetchPolicy: "network-only",
        });
        siteId = data.QAProductFamilies.find((family) => family.id === id)?.siteId;
      } else if (authorizationSiteResource === "qaErrorCatalogue") {
        const { data } = await apolloClient.query<ResolveQAErrorCatalogueAuthorizationSiteQuery>({
          query: ResolveQAErrorCatalogueAuthorizationSiteDocument,
          variables: { id },
          fetchPolicy: "network-only",
        });
        siteId = data.QAErrorCatalogues.find((catalogue) => catalogue.id === id)?.siteId;
      } else if (authorizationSiteResource === "bdeProductionPlanTemplate") {
        const { data } =
          await apolloClient.query<ResolveBDEProductionPlanTemplateAuthorizationSiteQuery>({
            query: ResolveBDEProductionPlanTemplateAuthorizationSiteDocument,
            variables: { id },
            fetchPolicy: "network-only",
          });
        siteId = data.BDEProductionPlanTemplates.find((template) => template.id === id)?.siteId;
      } else if (authorizationSiteResource === "user") {
        const { data } = await apolloClient.query<ResolveUserAuthorizationSiteQuery>({
          query: ResolveUserAuthorizationSiteDocument,
          variables: { id },
          fetchPolicy: "network-only",
        });
        siteId = data.Users.find((user) => user.id === id)?.siteId;
      } else if (authorizationSiteResource === "tcSiteConfig") {
        const { data } = await apolloClient.query<ResolveTCSiteConfigAuthorizationSiteQuery>({
          query: ResolveTCSiteConfigAuthorizationSiteDocument,
          variables: { id },
          fetchPolicy: "network-only",
        });
        siteId = data.TCGetSiteConfigs.find((config) => config.id === id)?.siteId;
      } else if (authorizationSiteResource === "bdeDataSource") {
        const { data } = await apolloClient.query<ResolveBDEDataSourceAuthorizationSiteQuery>({
          query: ResolveBDEDataSourceAuthorizationSiteDocument,
          variables: { id },
          fetchPolicy: "network-only",
        });
        siteId = data.BDEDataSources.find((source) => source.id === id)?.productionPlanTemplate
          .siteId;
      } else if (authorizationSiteResource === "bdeProcess") {
        const { data } = await apolloClient.query<ResolveBDEProcessAuthorizationSiteQuery>({
          query: ResolveBDEProcessAuthorizationSiteDocument,
          variables: { id },
          fetchPolicy: "network-only",
        });
        siteId = data.BDEProcesses.find((process) => process.id === id)?.productionPlanTemplate
          .siteId;
      } else if (authorizationSiteResource === "bdeLabel") {
        const { data } = await apolloClient.query<ResolveBDELabelAuthorizationSiteQuery>({
          query: ResolveBDELabelAuthorizationSiteDocument,
          variables: { id },
          fetchPolicy: "network-only",
        });
        const label = data.BDELabels.find((candidate) => candidate.id === id);
        if (
          !matchesAuthorizationParentId(
            label?.productionPlanTemplateId,
            to.params.productionPlanTemplateId,
          )
        ) {
          return "/404";
        }
        siteId = label?.BDEProductionPlanTemplate?.siteId;
      } else if (authorizationSiteResource === "bdeChecklistConnection") {
        const { data } =
          await apolloClient.query<ResolveBDEChecklistConnectionAuthorizationSiteQuery>({
            query: ResolveBDEChecklistConnectionAuthorizationSiteDocument,
            variables: { id },
            fetchPolicy: "network-only",
          });
        siteId = data.BDEGetChecklistConnection.productionPlanTemplate.siteId;
      } else if (authorizationSiteResource === "userShiftSchedule") {
        const { data } = await apolloClient.query<ResolveUserShiftScheduleAuthorizationSiteQuery>({
          query: ResolveUserShiftScheduleAuthorizationSiteDocument,
          variables: { id },
          fetchPolicy: "network-only",
        });
        siteId = data.UserShiftSchedules.find((schedule) => schedule.id === id)?.siteId;
      } else if (authorizationSiteResource === "news") {
        const { data } = await apolloClient.query<ResolveNewsAuthorizationSitesQuery>({
          query: ResolveNewsAuthorizationSitesDocument,
          variables: { id },
          fetchPolicy: "network-only",
        });
        siteIds = data.NewsAuthorizationSites;
      } else if (authorizationSiteResource === "bdeView") {
        const { data } = await apolloClient.query<ResolveBDEViewAuthorizationSiteQuery>({
          query: ResolveBDEViewAuthorizationSiteDocument,
          variables: { id },
          fetchPolicy: "network-only",
        });
        siteId = data.BDEViewAuthorizationSite ?? undefined;
      } else {
        const { data } = await apolloClient.query<ResolveCostCenterAuthorizationSiteQuery>({
          query: ResolveCostCenterAuthorizationSiteDocument,
          variables: { id },
          fetchPolicy: "network-only",
        });
        siteId = data.CostCenters.find((costCenter) => costCenter.id === id)?.siteId;
      }
      if (siteIds !== undefined) {
        if (
          siteIds.length === 0 ||
          siteIds.some((value) => !Number.isInteger(value) || value <= 0)
        ) {
          return "/404";
        }
        (to.meta as RouterAccessRightMetaType).resolvedAuthorizationSiteIds = siteIds;
      } else {
        if (!Number.isInteger(siteId) || (siteId ?? 0) <= 0) return "/404";
        (to.meta as RouterAccessRightMetaType).resolvedAuthorizationSiteId = siteId;
      }
    } catch {
      return "/404";
    }
  }
  // Check if the user has the needed right to go to his destination
  const hasAccess = routerRightCheck(
    to.meta as RouterAccessRightMetaType,
    (to.meta as RouterAccessRightMetaType).resolvedAuthorizationSiteId ??
      to.params.siteId ??
      (typeof to.query.siteId === "string" ? to.query.siteId : undefined),
  );
  if (!hasAccess) {
    // If the user is missing a right he should be redirected to a "neutral" site
    return "/404";
  }
  if (!from.meta?.notSaveLastPage) {
    store.dispatch("login/updateLastPage", from.path);
  }
  return true;
});

function getAllRouteNames(routesProp: RouteRecordRaw[]): string[] {
  const names: string[] = [];

  const walk = (records: RouteRecordRaw[]) => {
    for (const r of records) {
      if (r.name != null && typeof r.name === "string") {
        names.push(r.name);
      }

      if (r.children?.length) {
        walk(r.children);
      }
    }
  };

  walk(routesProp);
  return names;
}

function compareNames(
  first: string[],
  second: string[],
): {
  missingInSecond: string[];
  additionalInSecond: string[];
} {
  const firstSet = new Set(first);
  const secondSet = new Set(second);

  const missingInSecond = first.filter((name) => !secondSet.has(name));
  const additionalInSecond = second.filter((name) => !firstSet.has(name));

  if (missingInSecond.length > 0) {
    // eslint-disable-next-line no-console
    console.log(
      "Missing router translations:",
      missingInSecond.map((n) => `t('router.${n}')`),
    );
  }
  return { missingInSecond, additionalInSecond };
}

if (document.location.hostname === "localhost") {
  const names = getAllRouteNames(routes);
  void import("../translations/app/de.json").then(({ default: deTranslations }) => {
    const routerTranslations = Object.keys(deTranslations)
      .filter((key) => key.startsWith("router."))
      .map((key) => key.slice("router.".length));
    compareNames(
      names.filter((name) => name !== "achievements"),
      routerTranslations,
    );
  });
}
